import React, { useEffect, useState } from "react";
import { SC, ST, fmt, late, parseDate } from "./npdUtils";

// Side drawer with a part's details, stage timeline, actions and activity log
const PartDrawer = ({ part: p, open, app, busy, onAction, onClose }) => {
  const { isAdmin, me, buyers, today, go } = app;
  const [note, setNote] = useState("");
  const [jump, setJump] = useState(0);
  const [reassign, setReassign] = useState("");

  useEffect(() => {
    setNote("");
    setJump(p?.cur ?? 0);
    setReassign(p?.buyerId ?? "");
  }, [p?.id, p?.cur, p?.buyerId]);

  if (!p) return <aside className={`drawer${open ? " open" : ""}`} />;

  const N = p.L.stages.length;
  const own = !isAdmin && p.buyerId === me?.orgUserId;
  const dd = p.stageDates || {};
  const d = late(p, today);
  const act = (action, extra = {}) => onAction(action, { note, ...extra });

  const row = (s, i) => {
    const st = i < p.cur ? "d" : i === p.cur ? `cur ${SC[p.s]}` : "";
    const done = dd[i] ? parseDate(dd[i]) : null;
    const text = i < p.cur
      ? (done ? `Completed ${fmt(done)}` : "Completed")
      : i === p.cur
        ? (p.s === 3 ? "Stalled here, flagged delayed" : p.s === 2 ? "Awaiting review sign-off" : "In progress")
        : "Not started";
    const btn = own && i === p.cur && p.cur < N
      ? <button className="btn p s" disabled={busy} onClick={() => act("ADVANCE")}>Mark complete</button>
      : own && i === p.cur - 1
        ? <button className="btn s" disabled={busy} onClick={() => act("UNDO")}>Reopen</button>
        : null;
    return (
      <div key={i} className="st">
        <span className={`dot ${st}`} />
        <div style={{ flex: 1 }}><b>{i + 1}. {s}</b><br /><span className="mut">{text}</span></div>
        {btn}
      </div>
    );
  };

  return (
    <aside className={`drawer${open ? " open" : ""}`}>
      <button className="x" onClick={onClose}>Close</button>
      <h3 style={{ margin: 0 }}>{p.partNo}</h3>
      <p className="sub" style={{ marginBottom: 10 }}>{p.name} · {p.L.name}</p>
      <p><span className={`tag c-${SC[p.s]}`}>{ST[p.s]}</span></p>
      <p>
        Supplier:{" "}
        {p.supplierName
          ? <button className="lnk" onClick={() => go("suppliers")}>{p.supplierName}</button>
          : "-"}<br />
        Purchasing engineer: {p.buyerName}<br />
        Target date: {fmt(p.target)}{d > 0 && <span className="red"> ({d} days late)</span>}
      </p>

      {own ? (
        <>
          <h3>Update progress</h3>
          <textarea value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (required to mark delayed or change the stage)" />
          <div className="tools" style={{ marginTop: 8 }}>
            {p.s !== 1 && p.s !== 2 && <button className="btn" disabled={busy} onClick={() => act("REVIEW")}>Send for review</button>}
            {p.s !== 1 && p.s !== 3 && <button className="btn" disabled={busy} onClick={() => act("DELAY")}>Mark delayed</button>}
            {(p.s === 2 || p.s === 3) && <button className="btn" disabled={busy} onClick={() => act("RESUME")}>Resume work</button>}
          </div>
          {p.s !== 1 && (
            <div className="tools inl">
              <select style={{margin: "0"}} value={jump} onChange={(e) => setJump(Number(e.target.value))}>
                {p.L.stages.map((s, i) => <option key={i} value={i}>{i + 1}. {s}</option>)}
              </select>
              <button className="btn" disabled={busy} onClick={() => act("JUMP", { stage: jump })}>Change stage</button>
            </div>
          )}
        </>
      ) : isAdmin ? (
        <>
          <h3>Reassign</h3>
          <div className="tools inl">
            <select style={{margin: "0"}} value={reassign} onChange={(e) => setReassign(e.target.value)}>
              {!buyers.some((b) => b.orgUserId === p.buyerId) && <option value={p.buyerId || ""}>{p.buyerName || "Unassigned"}</option>}
              {buyers.map((b) => <option key={b.orgUserId} value={b.orgUserId}>{b.name}</option>)}
            </select>
            <button className="btn" disabled={busy} onClick={() => act("REASSIGN", { buyerId: reassign })}>Reassign</button>
          </div>
        </>
      ) : p.s !== 1 ? (
        <p className="sub">Assigned to {p.buyerName}. Only the assigned buyer can update this part.</p>
      ) : null}

      <h3>Development stages</h3>
      <p className="mut" style={{ margin: "0 0 6px" }}>{Math.min(p.cur, N)} of {N} stages completed</p>
      {p.L.stages.map(row)}

      {p.activities?.length > 0 && (
        <>
          <h3>Activity</h3>
          {p.activities.slice().reverse().map((l, i) => (
            <div key={i} className="st">
              <span className="dot" />
              <div><b>{l.text}</b><br /><span className="mut">{fmt(parseDate(l.date))} · {l.by}</span></div>
            </div>
          ))}
        </>
      )}
    </aside>
  );
};

export default PartDrawer;
